import { Validation } from '@zendeskgarden/react-forms/dist/typings/types'
import { capitalize, isArray, mergeWith } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useLocation } from 'react-router'

import { BaseRecord } from '../models/BaseRecord'
import { Estimate } from '../models/Estimate'
import { Invoice } from '../models/Invoice'
import { FKType } from '../models/SQLBuilder'
import { Source } from '../models/Source'
import { User } from '../models/User'
import { currencyFormat } from './numbers'

export const isEmpty = (user: any) => !user || Object.keys(user).length === 0

export const stringEqual = (item1: string | undefined, item2: string | undefined) =>
    item1 === item2 || (!item1 && !item2)

const lowestInUseZipCode = 501
export const isValidPostalCode = (zipcode?: string) =>
    zipcode && zipcode.match(/\d{5}/) && Number(zipcode) >= lowestInUseZipCode

export const isValidEmailAddress = (email?: string) =>
    email?.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,63}$/i)

export const isValidPhoneNumber = (phone?: string) => {
    if (!phone) return false

    const onlyNumbers = phone.replace(/[^\d]/g, '')
    // verify it doesn't start with 0 or 1
    return onlyNumbers?.match(/^[2-9]{1}[0-9]{9}$/i)
}

export async function batch<T>(
    objs: any[],
    callback: (obj: any) => Promise<T>,
    batchSize: number = 250
): Promise<T[]> {
    if (!objs?.length) return []
    const results: T[] = []
    for (let i = 0; i < objs?.length; i += batchSize) {
        const batch = objs.slice(i, i + batchSize)
        results.push(...(await Promise.all(batch.map(async (obj) => await callback(obj)))))
    }
    return results
}

export const isValidUrl = (url?: string): boolean => {
    // copied from https://www.regextester.com/93652
    return (
        !!url &&
        !!url
            .toLowerCase()
            .match(
                '^(http:\\/\\/www\\.|https:\\/\\/www\\.|http:\\/\\/|https:\\/\\/)?[a-z0-9]+([\\-\\.]{1}[a-z0-9]+)*\\.[a-z]{2,24}(:[0-9]{1,5})?(\\/.*)?$'
            )
    )
}

export const isValidInstagram = (url?: string): boolean => {
    return !!url && !!url.match(/^@?[\w](?!.*?\.{2})[\w.]{0,28}[\w]$/)
}

export const isValidUsername = (username?: string): boolean => {
    return !!username && !!username.match(/^[a-zA-Z0-9_-]{0,49}$/)
}

/** @deprecated */
export const isValid = (flag?: Validation) => flag === undefined || flag === 'success'

export const isUserValid = (user: any, emptyIsValid?: boolean) =>
    (emptyIsValid && isEmpty(user)) || containsRequiredUserKeys(user)

const requiredUserKeys = ['id', 'name', 'phone', 'signup_date', 'last_login_date']
const containsRequiredUserKeys = (user: any) =>
    requiredUserKeys.every(
        (expectedKey) => Object.keys(user).findIndex((key) => key === expectedKey) > -1
    )

export const capitalizeFirstLetters = (value: string, splitCharacter?: string) =>
    value
        .split(splitCharacter || ' ')
        .map((word) => capitalize(word))
        .join(splitCharacter || ' ')

export enum SourceType {
    AnyCard,
    Debit,
    Ach,
    Credit,
}

export const determineDefaultSource = (
    type: SourceType,
    sources: Array<Source>
): Source | undefined =>
    sources
        .sort((source) =>
            source.createdDate === undefined ? Infinity : source.createdDate.valueOf()
        )
        .find((source) => {
            if (
                type === SourceType.AnyCard &&
                ['debit', 'credit', 'prepaid'].includes(source.type ?? '')
            ) {
                return true
            }
            else if (type === SourceType.Debit && source.verifiedDate) {
                return true
            }
            else if (
                type === SourceType.Ach &&
                source.type === 'checking' &&
                source.verifiedDate &&
                source.charge
            ) {
                return true
            }
            else if (
                type === SourceType.Credit &&
                source.type === 'checking' &&
                source.transfer
            ) {
                return true
            }
            return false
        })

export const useQuery = () => {
    const { search } = useLocation()

    return React.useMemo(() => new URLSearchParams(search), [search])
}

export const Result = (obj?: any) => Promise.resolve(obj)

export const addImage = (data: any, image?: File) => {
    let formData: FormData | undefined = undefined
    if (image) {
        formData = new FormData()
        formData.append('image', image, image!.name)

        Object.keys(data).forEach((key: string) => {
            const value = data[key]
            if (typeof value === 'object' && value !== null) {
                if (Array.isArray(value)) {
                    value.forEach((valueObj) =>
                        formData!.append(
                            key,
                            typeof valueObj === 'object' ? JSON.stringify(valueObj) : valueObj
                        )
                    )
                }
                else if ('toJSON' in value && typeof value.toJSON === 'function')
                    formData!.append(key, value.toJSON())
                else formData!.append(key, JSON.stringify(value))
            }
            else if (value != null) formData!.append(key, value)
        })
    }
    return formData ?? data
}

export const base64toFile = (b64Data: string, contentType: string, fileName?: string) => {
    const sliceSize = 512

    var byteCharacters = atob(b64Data)
    var byteArrays = []

    for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        var slice = byteCharacters.slice(offset, offset + sliceSize)

        var byteNumbers = new Array(slice.length)
        for (var i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i)
        }

        var byteArray = new Uint8Array(byteNumbers)

        byteArrays.push(byteArray)
    }

    return new File(byteArrays, fileName ?? 'image', { type: contentType })
}

export const PERM_ROLE_CLIENT = 0
export const PERM_ROLE_CONTRACTOR = 1
export const PERM_ROLE_EMPLOYEE = 2
export const PERM_ROLE_ADMIN = 3
export const PERM_ROLE_OWNER = 4
export const PERM_ROLE_PARTNER = 5

export const userPermission = (loggedInUser: User) => {
    if (['solo', 'company'].includes(loggedInUser.type || '')) {
        return PERM_ROLE_OWNER
    }
    if (loggedInUser.type === 'client') {
        return PERM_ROLE_CLIENT
    }
    if (loggedInUser.type === 'partner') {
        return PERM_ROLE_PARTNER
    }

    return (
        {
            admin: PERM_ROLE_ADMIN,
            employee: PERM_ROLE_EMPLOYEE,
            contractor: PERM_ROLE_CONTRACTOR,
        }[loggedInUser.role || ''] ?? PERM_ROLE_CLIENT
    )
}

export const isPastDate = (
    date: string | Date | moment.Moment,
    hoursBeforeDate?: number | string
) => {
    const dueDate = hoursBeforeDate ? moment(date).add(-Number(hoursBeforeDate), 'hours') : date
    return moment().isAfter(dueDate)
}

export const formatHourMinute = (duration: number) => {
    const hours = Math.floor(duration / 60)
    const minutes = duration % 60
    let minutePart = ''
    let hourPart = ''
    if (minutes) {
        minutePart = `${minutes} ${minutes > 1 ? 'mins' : 'min'}`
    }
    if (hours) {
        hourPart = `${hours} ${hours > 1 ? 'hours' : 'hour'}`
    }
    return `${hourPart}${hourPart ? ' ' : ''}${minutePart}`
}

export const discountDisplay = (record: Invoice | Estimate) => {
    let discountAmount = record.discountAmount

    let displayAmount = ''
    let description = ''
    if (record.discountType === 'rate') {
        displayAmount = currencyFormat(discountAmount)
        description = `Discount (${currencyFormat(discountAmount)})`
    }
    else if (record.discountType === 'percent' && Number(discountAmount) > 0) {
        displayAmount = `${record.discount}%`
        description = `Discount (${record.discount}%)`
    }
    else {
        discountAmount = 0
    }

    return [displayAmount, description, discountAmount]
}

export const reorderArray = <T extends any>(list: T[], startIndex: number, endIndex: number) => {
    const result = [...list]
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
}

export const sortBySeqnum = <T extends { seqnum?: number }>(list: T[]) => {
    return list.sort((a, b) => ((a.seqnum ?? 1) < (b.seqnum ?? 1) ? -1 : 1))
}

export const mergeFk = (...objects: FKType[]): FKType => {
    return mergeWith({}, ...objects, (objValue: any, srcValue: any) => {
        if (isArray(objValue) && isArray(srcValue)) {
            return Array.from(new Set([...objValue, ...srcValue]))
        }
    })
}

export const computeHash = (str: string) => {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i)
    }
    return hash
}
